"use client";

import { useMemo } from "react";
import { useTable } from "spacetimedb/react";
import { toast } from "sonner";
import { tables } from "@/module_bindings";
import { useRowTransitions } from "@/hooks/spacetime/use-row-transitions";
import {
  getProductNameByProduct,
  getSubscriptionProductIdByProduct,
} from "@/types/subscription";
import type { Product } from "@/types/subscription";
import type { Organization } from "@/types/organization";
import type { OrgSubscriptionRow } from "@/lib/stdb-map";

/** Product display name for a Stripe product id; null when unrecognized. */
function productNameByProductId(productId: string): string | null {
  const products: Product[] = ["poweron-pro", "poweron-pipelines"];
  for (const product of products) {
    if (getSubscriptionProductIdByProduct(product) === productId) {
      return getProductNameByProduct(product);
    }
  }
  return null;
}

/**
 * Spec event #1: `org_subscriptions.status: active -> canceled` for the
 * current org. Covers both cancellation paths by construction — plain
 * `customer.subscription.deleted` and customer deletion both tombstone the
 * row to 'canceled'. Subscription creation intentionally does not toast.
 */
export function SubscriptionCanceledToasts({
  organization,
}: {
  organization: Organization | null;
}) {
  const [rows, isReady] = useTable(tables.org_subscriptions);
  const orgRows = useMemo(
    () =>
      organization
        ? rows.filter(
            (row: OrgSubscriptionRow) => row.organizationId === organization.id,
          )
        : [],
    [rows, organization],
  );

  useRowTransitions<OrgSubscriptionRow>({
    rows: orgRows,
    isReady,
    getId: (row) => row.id,
    getState: (row) => row.status,
    resetKey: organization?.id ?? null,
    onTransition: ({ next, row }) => {
      if (next !== "canceled") return;
      const productName = productNameByProductId(row.productId);
      toast.warning("Subscription Canceled", {
        id: `sub-canceled-${row.id}`,
        description: productName
          ? `Your ${productName} subscription has been canceled.`
          : "Your subscription has been canceled.",
      });
    },
  });

  return null;
}
